import React, { useDeferredValue, useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  InputAdornment,
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react/dist/iconify.js';
import { SX, MEDALS, BADGES_BY_KEY, initialsOf, levelProgress } from './leaderboardData';

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

/* --- static styles, hoisted -------------------------------------------- *
 * Every inline sx object is a fresh identity that emotion has to hash and
 * cache-miss on. In a table that is per cell per row, so the ones that never
 * vary live up here and are shared by every row.
 * ----------------------------------------------------------------------- */
const CARD_SX = {
  borderRadius: `${SX.radius.lg}px`,
  background: SX.card,
  border: `1px solid ${SX.borderStrong}`,
  boxShadow: SX.shadowSoft,
  overflow: 'hidden',
};

const HEAD_CELL = {
  fontSize: 11.5,
  fontWeight: 800,
  color: SX.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  borderBottom: `1px solid ${SX.border}`,
  background: SX.cardSoft,
  py: 1.4,
};
const HEAD_RANK = { ...HEAD_CELL, width: 110 };
const HEAD_XP = { ...HEAD_CELL, textAlign: 'right', width: 120 };
const HEAD_LEVEL = { ...HEAD_CELL, width: 210 };
const HEAD_BADGES = { ...HEAD_CELL, width: 190 };

const BODY_CELL = { borderBottom: `1px solid ${SX.border}`, py: 1.25 };
const BODY_CELL_RIGHT = { ...BODY_CELL, textAlign: 'right' };

const RANK_PLAIN_SX = {
  width: 34,
  height: 34,
  borderRadius: `${SX.radius.sm}px`,
  background: 'rgba(27,36,56,0.04)',
  color: SX.textMuted,
  fontWeight: 800,
  fontSize: 14,
};

const AVATAR_SX = {
  width: 40,
  height: 40,
  fontSize: 14,
  fontWeight: 800,
  color: '#fff',
  background: SX.brandGradient,
};

const PROGRESS_SX = {
  height: 5,
  borderRadius: 999,
  background: 'rgba(27,36,56,0.07)',
};

const BADGE_DOT_SX = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'transform .15s ease',
  '&:hover': { transform: 'translateY(-2px)' },
};

/** Up to four badge glyphs, then a +N — a full shelf would crowd the row. */
const BadgeStrip = React.memo(function BadgeStrip({ badges = [] }) {
  if (!badges.length) {
    return <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>—</Typography>;
  }
  const shown = badges.slice(0, 4);
  const extra = badges.length - shown.length;

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      {shown.map((key) => {
        const badge = BADGES_BY_KEY[key];
        if (!badge) return null;
        return (
          <Tooltip key={key} arrow title={`${badge.name} — ${badge.description}`}>
            <Box
              sx={{
                ...BADGE_DOT_SX,
                background: `${badge.color}12`,
                border: `1px solid ${badge.color}30`,
                color: badge.color,
              }}
            >
              <Icon icon={badge.icon} width={15} height={15} />
            </Box>
          </Tooltip>
        );
      })}
      {extra > 0 && (
        <Tooltip
          arrow
          title={badges
            .slice(4)
            .map((k) => BADGES_BY_KEY[k]?.name)
            .filter(Boolean)
            .join(', ')}
        >
          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: SX.textMuted, cursor: 'default' }}>
            +{extra}
          </Typography>
        </Tooltip>
      )}
    </Stack>
  );
});

/** Medal glyph for the top three, plain number below that. */
const RankCell = React.memo(function RankCell({ rank }) {
  const medal = MEDALS[rank];

  if (medal) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: `${SX.radius.sm}px`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `${medal.color}18`,
            border: `1px solid ${medal.color}40`,
            color: medal.color,
          }}
        >
          <Icon icon={medal.icon} width={19} height={19} />
        </Box>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: medal.color }}>{medal.label}</Typography>
      </Stack>
    );
  }

  return (
    <Stack alignItems="center" justifyContent="center" sx={RANK_PLAIN_SX}>
      {rank}
    </Stack>
  );
});

/**
 * One standings row. Memoised on the student object, which is rebuilt only
 * when the batch is refetched — so paging, searching and hovering re-render
 * the shell, not every row that was already on screen.
 */
const LeaderboardRow = React.memo(function LeaderboardRow({ student }) {
  const medal = MEDALS[student.rank];
  const progress = levelProgress(student.xp);

  return (
    <TableRow
      hover
      sx={{
        background: medal ? `${medal.color}08` : 'transparent',
        '& td:first-of-type': medal ? { boxShadow: `inset 3px 0 0 ${medal.color}` } : undefined,
      }}
    >
      {/* rank */}
      <TableCell sx={BODY_CELL}>
        <RankCell rank={student.rank} />
      </TableCell>

      {/* identity: picture + name */}
      <TableCell sx={BODY_CELL}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar src={student.image || undefined} alt={student.name} sx={AVATAR_SX}>
            {initialsOf(student.name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 14, fontWeight: 700, color: SX.text }}>
              {student.name}
            </Typography>
            {student.admissionNo != null && (
              <Stack direction="row" alignItems="center" spacing={0.4}>
                <Icon icon="solar:user-id-linear" width={13} height={13} color={SX.textMuted} />
                <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>{student.admissionNo}</Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </TableCell>

      {/* xp */}
      <TableCell sx={BODY_CELL_RIGHT}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5}>
          <Icon
            icon="solar:bolt-bold-duotone"
            width={15}
            height={15}
            color={student.xp > 0 ? SX.blue : SX.textMuted}
          />
          <Typography
            sx={{ fontSize: 15, fontWeight: 800, color: student.xp > 0 ? SX.text : SX.textMuted }}
          >
            {student.xp.toLocaleString()}
          </Typography>
        </Stack>
      </TableCell>

      {/* level + progress to the next tier */}
      <TableCell sx={BODY_CELL}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            width: 'fit-content',
            px: 0.9,
            py: 0.3,
            borderRadius: 999,
            background: `${student.level.color}14`,
            border: `1px solid ${student.level.color}33`,
            color: student.level.color,
          }}
        >
          <Icon icon={student.level.icon} width={14} height={14} />
          <Typography sx={{ fontSize: 11.5, fontWeight: 800 }}>{student.level.name}</Typography>
        </Stack>

        <Tooltip
          arrow
          title={
            progress.next
              ? `${progress.toNext.toLocaleString()} XP to ${progress.next.name}`
              : 'Top of the ladder'
          }
        >
          <Box sx={{ mt: 0.75, width: 150 }}>
            <LinearProgress
              variant="determinate"
              value={progress.pct}
              sx={{
                ...PROGRESS_SX,
                '& .MuiLinearProgress-bar': { borderRadius: 999, background: student.level.color },
              }}
            />
          </Box>
        </Tooltip>
      </TableCell>

      {/* badges */}
      <TableCell sx={BODY_CELL}>
        <BadgeStrip badges={student.badges} />
      </TableCell>
    </TableRow>
  );
});

/**
 * The full ranked standings for one batch: Rank, Student (picture + name),
 * XP, Current Level (with progress to the next tier), Badges Earned. Rows are
 * mapped and ranked upstream from the leaderboard API.
 *
 * Search and paging are client-side and deliberately so: one batch's roster is
 * a bounded list that arrives in a single response, and ranks are absolute —
 * filtering to one learner still shows the rank they hold in the whole batch,
 * which server-side paging would have to compute and send instead. What paging
 * buys is the DOM: a 400-learner batch mounts 25 rows, not 400.
 */
export default function LeaderboardTable({ rows = [], loading, batchName, batchId }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  // Keeps typing responsive on a long roster: the field updates immediately,
  // the (expensive) filtered re-render is allowed to lag a frame behind.
  const deferredSearch = useDeferredValue(search);

  // A new batch resets the view. Keyed on the batch, not on `rows`: the page
  // serves cached standings and revalidates behind them, so `rows` gets a new
  // identity while the mentor is looking at the same batch — resetting on that
  // would clear a search they had just typed.
  useEffect(() => {
    setPage(0);
    setSearch('');
  }, [batchId]);

  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) || String(r.admissionNo ?? '').toLowerCase().includes(q),
    );
  }, [rows, deferredSearch]);

  // Clamp the page when a filter shortens the list under the current offset.
  const pageCount = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const safePage = Math.min(page, pageCount - 1);

  const paged = useMemo(
    () => filtered.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage),
    [filtered, safePage, rowsPerPage],
  );

  const earning = useMemo(() => rows.filter((r) => r.xp > 0).length, [rows]);

  return (
    <Box sx={CARD_SX}>
      {/* card header */}
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={{ xs: 'stretch', md: 'center' }}
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: 2.25, py: 1.75, borderBottom: `1px solid ${SX.border}` }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: `${SX.radius.sm}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `${SX.blue}12`,
              color: SX.blue,
              flexShrink: 0,
            }}
          >
            <Icon icon="solar:ranking-bold-duotone" width={19} height={19} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: 14.5, fontWeight: 800, color: SX.text }}>
              {batchName || 'Batch standings'}
            </Typography>
            <Typography sx={{ fontSize: 11.5, color: SX.textMuted }}>
              Ordered by XP earned in this batch
            </Typography>
          </Box>
        </Stack>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={1}
          sx={{ flexShrink: 0 }}
        >
          {!loading && rows.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  px: 1,
                  py: 0.4,
                  borderRadius: 999,
                  background: SX.cardSoft,
                  border: `1px solid ${SX.border}`,
                  color: SX.textMuted,
                }}
              >
                <Icon icon="solar:users-group-rounded-bold-duotone" width={14} height={14} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>{rows.length} learners</Typography>
              </Stack>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  px: 1,
                  py: 0.4,
                  borderRadius: 999,
                  background: 'rgba(46,125,98,0.10)',
                  border: '1px solid rgba(46,125,98,0.24)',
                  color: '#2E7D62',
                }}
              >
                <Icon icon="solar:bolt-bold-duotone" width={14} height={14} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>{earning} earning</Typography>
              </Stack>
            </Stack>
          )}

          <TextField
            size="small"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search learner or adm. no"
            autoComplete="off"
            sx={{ minWidth: { sm: 230 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="solar:magnifer-bold-duotone" width={16} height={16} color={SX.textMuted} />
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      <TableContainer sx={{ maxHeight: 620 }}>
        <Table stickyHeader size="medium">
          <TableHead>
            <TableRow>
              <TableCell sx={HEAD_RANK}>Rank</TableCell>
              <TableCell sx={HEAD_CELL}>Student</TableCell>
              <TableCell sx={HEAD_XP}>XP</TableCell>
              <TableCell sx={HEAD_LEVEL}>Current Level</TableCell>
              <TableCell sx={HEAD_BADGES}>Badges Earned</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 7, textAlign: 'center', border: 'none' }}>
                  <CircularProgress size={26} />
                </TableCell>
              </TableRow>
            )}

            {!loading && !rows.length && (
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 7, textAlign: 'center', border: 'none' }}>
                  <Stack alignItems="center" spacing={1}>
                    <Icon icon="solar:ranking-linear" width={34} height={34} color={SX.textMuted} />
                    <Typography sx={{ fontSize: 13.5, color: SX.textMuted }}>
                      Select a batch to see its standings.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {!loading && rows.length > 0 && !filtered.length && (
              <TableRow>
                <TableCell colSpan={5} sx={{ py: 7, textAlign: 'center', border: 'none' }}>
                  <Stack alignItems="center" spacing={1}>
                    <Icon icon="solar:magnifer-bold-duotone" width={30} height={30} color={SX.textMuted} />
                    <Typography sx={{ fontSize: 13.5, color: SX.textMuted }}>
                      No learner matches “{search}”.
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            )}

            {!loading &&
              paged.map((student) => <LeaderboardRow key={student.id} student={student} />)}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && filtered.length > ROWS_PER_PAGE_OPTIONS[0] && (
        <TablePagination
          component="div"
          count={filtered.length}
          page={safePage}
          onPageChange={(e, next) => setPage(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
          sx={{ borderTop: `1px solid ${SX.border}` }}
        />
      )}
    </Box>
  );
}
